import { createFileRoute } from "@tanstack/react-router";
import { BookOpen, ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { KnowledgeKind, KnowledgeSourceView } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { ChunksDialog } from "@/components/knowledge/ChunksDialog";
import { SourceDialog, type SourceDialogState } from "@/components/knowledge/SourceDialog";
import { ICONE_DO_TIPO } from "@/components/knowledge/icons";
import { PlaybookLearnCard } from "@/components/knowledge/PlaybookLearnCard";
import { PlaybookViewer } from "@/components/knowledge/PlaybookViewer";
import { SourceList, type SourceActions } from "@/components/knowledge/SourceList";
import { TestPanel } from "@/components/knowledge/TestPanel";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import {
  knowledgeError,
  useDeleteSource,
  useKnowledge,
  useSyncSource,
  useUpdateSource,
} from "@/lib/knowledge";

export const Route = createFileRoute("/_authenticated/conhecimento")({
  component: ConhecimentoPage,
});

/** Os cartões de "adicionar", na ordem do que mais ajuda a IA. */
const ADICIONAR: { kind: KnowledgeKind; titulo: string; texto: string }[] = [
  { kind: "faq", titulo: "Perguntas frequentes", texto: "Pergunta e resposta, uma por linha." },
  { kind: "prices", titulo: "Tabela de preços", texto: "A IA só cita preço que estiver aqui." },
  { kind: "website", titulo: "Site", texto: "Lê até 30 páginas do seu site." },
  { kind: "file", titulo: "Arquivo PDF", texto: "PDF, TXT ou MD com texto." },
  { kind: "text", titulo: "Texto livre", texto: "Endereço, horários, como funciona." },
];

/**
 * Conhecimento da IA: o que o agente de IA dos fluxos sabe da clínica.
 *
 * A página responde três perguntas, nesta ordem: o que eu posso ensinar
 * (cartões), o que a IA já sabe e se deu certo (lista com status) e como ela
 * responde com isso (teste). "Roteiros aprendidos" ficam à parte: são
 * gerados das conversas e só entram quando alguém revisa e liga.
 */
function ConhecimentoPage() {
  const { can } = useAuth();
  const podeEditar = can("admin", "manager");

  const base = useKnowledge();
  const atualizar = useUpdateSource();
  const sincronizar = useSyncSource();
  const excluir = useDeleteSource();

  const [dialogo, setDialogo] = useState<SourceDialogState>(null);
  const [pedacosDe, setPedacosDe] = useState<KnowledgeSourceView | null>(null);
  const [excluindo, setExcluindo] = useState<KnowledgeSourceView | null>(null);
  // Guarda o id, não a fonte: o visualizador acompanha a lista (status e
  // "ligado" mudam enquanto ele está aberto).
  const [roteiroAberto, setRoteiroAberto] = useState<string | null>(null);

  const fontes = useMemo(
    () => (base.data?.sources ?? []).filter((s) => s.kind !== "playbook"),
    [base.data],
  );
  const roteiros = useMemo(
    () => (base.data?.sources ?? []).filter((s) => s.kind === "playbook"),
    [base.data],
  );

  const acoes: SourceActions = {
    editar: (s) => setDialogo({ mode: "edit", source: s }),
    verPedacos: (s) => setPedacosDe(s),
    sincronizar: (s) =>
      sincronizar.mutate(s.id, {
        onSuccess: () => toast.success("Na fila para ler de novo"),
        onError: (e) => toast.error(knowledgeError(e, "Não foi possível sincronizar")),
      }),
    ligar: (s, enabled) =>
      atualizar.mutate(
        { id: s.id, enabled },
        {
          onSuccess: () =>
            toast.success(
              enabled ? "A IA volta a usar esta fonte" : "A IA deixa de usar esta fonte",
            ),
          onError: (e) => toast.error(knowledgeError(e, "Não foi possível alterar")),
        },
      ),
    excluir: (s) => setExcluindo(s),
  };

  // Roteiro abre no visualizador legível, não na lista de pedaços.
  const acoesDosRoteiros: SourceActions = {
    ...acoes,
    verPedacos: (s) => setRoteiroAberto(s.id),
  };
  const roteiro = roteiros.find((r) => r.id === roteiroAberto) ?? null;

  const total = base.data?.enabledChars ?? 0;
  const limite = base.data?.fullContextLimit ?? 12_000;

  return (
    <>
      <PageHeader
        title="Conhecimento da IA"
        description="O que a IA sabe sobre a sua clínica. Ela responde dúvidas só com o que estiver aqui — o resto, passa para a equipe."
      />

      <PageBody className="space-y-5">
        {podeEditar && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {ADICIONAR.map((a) => {
              const Icone = ICONE_DO_TIPO[a.kind];
              return (
                <button
                  key={a.kind}
                  type="button"
                  onClick={() => setDialogo({ mode: "create", kind: a.kind })}
                  className="surface-panel flex items-start gap-3 bg-card p-3.5 text-left shadow-card transition hover:-translate-y-px hover:shadow-panel"
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Icone className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{a.titulo}</span>
                    <span className="block text-xs text-muted-foreground">{a.texto}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_26rem]">
          <div className="space-y-5">
            <Card className="overflow-hidden">
              <CardHeader className="border-b">
                <div>
                  <CardTitle>Fontes</CardTitle>
                  <CardDescription>
                    {base.data ? (
                      <>
                        <span className="tabular-nums">{total.toLocaleString("pt-BR")}</span>{" "}
                        caracteres ligados ·{" "}
                        {total <= limite
                          ? "cabe inteiro na conversa: a IA lê tudo a cada resposta"
                          : "base grande: a IA busca os trechos de cada pergunta"}
                      </>
                    ) : (
                      "Carregando…"
                    )}
                  </CardDescription>
                </div>
              </CardHeader>
              {base.isPending ? (
                <div className="divide-y" aria-busy>
                  {Array.from({ length: 3 }, (_, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                      <Skeleton className="size-9 rounded-lg" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-48 max-w-[60%]" />
                        <Skeleton className="h-3 w-64 max-w-[80%]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : base.isError ? (
                <EmptyState
                  icon={BookOpen}
                  title="Não foi possível carregar a base"
                  description="Confira a conexão e tente de novo."
                  action={
                    <Button variant="outline" size="sm" onClick={() => void base.refetch()}>
                      Tentar de novo
                    </Button>
                  }
                />
              ) : fontes.length === 0 ? (
                <EmptyState
                  icon={BookOpen}
                  title="A IA ainda não sabe nada da clínica"
                  description="Comece pelas perguntas frequentes e pela tabela de preços — são o que as pacientes mais perguntam. Depois, o site. Sem base, a IA dos fluxos responde dúvidas com “vou confirmar com a equipe”."
                  action={
                    podeEditar ? (
                      <Button size="sm" onClick={() => setDialogo({ mode: "create", kind: "faq" })}>
                        Adicionar perguntas frequentes
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <SourceList sources={fontes} actions={acoes} canEdit={podeEditar} />
              )}
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="border-b">
                <div>
                  <CardTitle>Roteiros aprendidos</CardTitle>
                  <CardDescription>
                    Como a equipe conduz as conversas, extraído do histórico. Revise antes de ligar:
                    roteiro ligado vira conhecimento da IA.
                  </CardDescription>
                </div>
              </CardHeader>
              {podeEditar && <PlaybookLearnCard onOpen={(id) => setRoteiroAberto(id)} />}
              {roteiros.length === 0 ? (
                <EmptyState
                  compact
                  icon={ScrollText}
                  title="Nenhum roteiro ainda"
                  description={
                    podeEditar
                      ? "Gere o primeiro acima: a IA aprende com as conversas que viraram agendamento na sua clínica."
                      : "Os roteiros aparecem aqui quando a gestão gerar um a partir das conversas da equipe."
                  }
                />
              ) : (
                <SourceList sources={roteiros} actions={acoesDosRoteiros} canEdit={podeEditar} />
              )}
            </Card>
          </div>

          <div className="space-y-5 xl:sticky xl:top-0 xl:self-start">
            <TestPanel podeTestarIa={podeEditar} />
          </div>
        </div>
      </PageBody>

      <SourceDialog
        key={
          dialogo
            ? dialogo.mode === "edit"
              ? dialogo.source.id
              : `novo-${dialogo.kind}`
            : "fechado"
        }
        state={dialogo}
        onClose={() => setDialogo(null)}
      />
      <ChunksDialog source={pedacosDe} onClose={() => setPedacosDe(null)} />
      <PlaybookViewer
        source={roteiro}
        canEdit={podeEditar}
        toggling={atualizar.isPending}
        onClose={() => setRoteiroAberto(null)}
        onToggle={(s, enabled) => acoes.ligar(s, enabled)}
        onEdit={(s) => {
          setRoteiroAberto(null);
          setDialogo({ mode: "edit", source: s });
        }}
        onDelete={(s) => {
          setRoteiroAberto(null);
          setExcluindo(s);
        }}
      />

      <Dialog open={Boolean(excluindo)} onOpenChange={(o) => !o && setExcluindo(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir “{excluindo?.title}”?</DialogTitle>
            <DialogDescription>
              A IA deixa de saber o que estava nesta fonte. Se for só por um tempo, desligue em vez
              de excluir.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={excluir.isPending}
              onClick={() =>
                excluindo &&
                excluir.mutate(excluindo.id, {
                  onSuccess: () => {
                    toast.success("Fonte excluída");
                    setExcluindo(null);
                  },
                  onError: (e) => {
                    toast.error(knowledgeError(e, "Não foi possível excluir"));
                    setExcluindo(null);
                  },
                })
              }
            >
              {excluir.isPending ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
